import { useState, useEffect, useCallback } from 'react'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'

export interface ColumnConfiguration {
  key: string
  name: string
  width?: string
  visible: boolean
  order: number
  align?: 'left' | 'center' | 'right'
  sortable?: boolean
  resizable?: boolean
}

export interface ViewSettings {
  viewMode: 'cards' | 'table' | 'compact'
  tableColumns?: string[]
  columnConfigurations?: ColumnConfiguration[]
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  filterBy?: Record<string, any>
  groupBy?: string
  itemsPerPage?: number
}

export interface ViewPreset {
  id: number
  name: string
  description?: string
  page_component: string
  preset_settings: ViewSettings
  is_global: boolean
  created_by: number
}

export interface ViewPresetResponse {
  presets: ViewPreset[]
  currentPresetId: number | null
}

export function useViewSettings(pageComponent: string) {
  const [settings, setSettings] = useState<ViewSettings | null>(null)
  const [presets, setPresets] = useState<ViewPreset[]>([])
  const [currentPresetId, setCurrentPresetId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch view settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall(`api/view-settings/${pageComponent}`)
      if (response.ok) {
        const data = await response.json()
        setSettings(data.settings)
      } else {
        throw new Error('Failed to fetch view settings')
      }
    } catch (err) {
      console.error('Error fetching view settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch view settings')
      
      // Set default settings on error - app remains functional
      const defaultSettings = getDefaultSettings(pageComponent)
      setSettings(defaultSettings)
      console.log('Using default settings due to database unavailability:', defaultSettings)
    } finally {
      setLoading(false)
    }
  }, [pageComponent])

  // Fetch view presets
  const fetchPresets = useCallback(async () => {
    try {
      const response = await makeApiCall(`api/view-settings/${pageComponent}/presets`)
      if (response.ok) {
        const data: ViewPresetResponse = await response.json()
        setPresets(data.presets)
        setCurrentPresetId(data.currentPresetId)
      }
    } catch (err) {
      console.error('Error fetching view presets:', err)
    }
  }, [pageComponent])

  // Update view settings
  const updateSettings = useCallback(async (newSettings: ViewSettings) => {
    try {
      setError(null)

      const response = await makeJsonApiCall(`/api/view-settings/${pageComponent}`, 'PUT', {
        settings: newSettings
      })

      if (response.ok) {
        setSettings(newSettings)
        // Clear current preset selection if settings were manually changed
        setCurrentPresetId(null)
        return true
      } else {
        throw new Error('Failed to update view settings')
      }
    } catch (err) {
      console.error('Error updating view settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to update view settings')
      return false
    }
  }, [pageComponent])

  // Apply a preset
  const applyPreset = useCallback(async (presetId: number) => {
    try {
      setError(null)

      const response = await makeJsonApiCall(
        `/api/view-settings/${pageComponent}/presets/${presetId}`,
        'POST',
        {}
      )

      if (response.ok) {
        const data = await response.json()
        setSettings(data.settings)
        setCurrentPresetId(presetId)
        return true
      } else {
        throw new Error('Failed to apply preset')
      }
    } catch (err) {
      console.error('Error applying preset:', err)
      setError(err instanceof Error ? err.message : 'Failed to apply preset')
      return false
    }
  }, [pageComponent])

  // Toggle view mode (legacy for backward compatibility)
  const toggleViewMode = useCallback(() => {
    if (settings) {
      const newMode = settings.viewMode === 'cards' ? 'table' : 'cards'
      updateSettings({ ...settings, viewMode: newMode })
    }
  }, [settings, updateSettings])

  // Set specific view mode
  const setViewMode = useCallback((mode: 'cards' | 'table' | 'compact') => {
    if (settings) {
      updateSettings({ ...settings, viewMode: mode })
    }
  }, [settings, updateSettings])

  // Update column configurations
  const updateColumnConfigurations = useCallback((columns: ColumnConfiguration[]) => {
    if (settings) {
      updateSettings({ 
        ...settings, 
        columnConfigurations: columns,
        tableColumns: columns.filter(col => col.visible).sort((a, b) => a.order - b.order).map(col => col.key)
      })
    }
  }, [settings, updateSettings])

  // Reset to default columns
  const resetToDefaultColumns = useCallback(() => {
    if (settings) {
      const defaultSettings = getDefaultSettings('deals_board')
      updateSettings({ 
        ...settings, 
        tableColumns: defaultSettings.tableColumns,
        columnConfigurations: undefined
      })
    }
  }, [settings, updateSettings])

  // Initialize
  useEffect(() => {
    fetchSettings()
    fetchPresets()
  }, [fetchSettings, fetchPresets])

  return {
    settings,
    presets,
    currentPresetId,
    loading,
    error,
    updateSettings,
    applyPreset,
    toggleViewMode,
    setViewMode,
    updateColumnConfigurations,
    resetToDefaultColumns,
    refetch: () => {
      fetchSettings()
      fetchPresets()
    }
  }
}

// Default settings for different page components
function getDefaultSettings(pageComponent: string): ViewSettings {
  switch (pageComponent) {
    case 'deals_board':
      return {
        viewMode: 'cards',
        tableColumns: ['name', 'customer_name', 'amount', 'deal_type', 'net_commission', 'expected_close_date'],
        sortBy: 'position',
        sortOrder: 'asc',
        filterBy: {},
        groupBy: 'status'
      }
    case 'tasks_board':
      return {
        viewMode: 'cards',
        tableColumns: ['title', 'customer_name', 'due_date', 'priority', 'status'],
        sortBy: 'due_date',
        sortOrder: 'asc',
        filterBy: {},
        groupBy: 'status'
      }
    case 'customers_list':
      return {
        viewMode: 'table',
        tableColumns: ['name', 'email', 'phone', 'created_at'],
        sortBy: 'name',
        sortOrder: 'asc',
        filterBy: {},
        itemsPerPage: 25
      }
    case 'leads_page':
      return {
        viewMode: 'cards',
        tableColumns: ['name', 'email', 'phone', 'status_name', 'urgency_name', 'task_count', 'created_at'],
        sortBy: 'created_at',
        sortOrder: 'desc',
        filterBy: {},
        itemsPerPage: 25
      }
    default:
      return {
        viewMode: 'cards',
        sortBy: 'created_at',
        sortOrder: 'desc',
        filterBy: {}
      }
  }
}